import { useState, useEffect } from 'react'
import { Plus, Search, Edit, Upload, X, Power, PowerOff, Download, User as UserIcon, Camera, Eye, EyeOff, Users, Sparkles, Filter, Mail, Phone, BadgeCheck } from 'lucide-react'
import { employeesAPI, districtsAPI, centersAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'

const emptyForm = {
  employee_id: '', full_name: '', email: '', mobile: '', password: '', role: 'EMPLOYEE',
  designation: '', district: '', training_centre: '', employment_status: 'ACTIVE',
}

export default function EmployeesPage() {
  const { success, error: toastError } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [employees, setEmployees] = useState<any[]>([])
  const [districts, setDistricts] = useState<any[]>([])
  const [centers, setCenters] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<any | null>(null)
  const [form, setForm] = useState({ ...emptyForm })
  const [isSaving, setIsSaving] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<any | null>(null)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)

  // Real-time validation error states
  const [emailError, setEmailError] = useState('')
  const [mobileError, setMobileError] = useState('')

  // Photo modal states
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null)
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('')

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false)

  const loadEmployees = async (search = '', pageArg = page) => {
    setIsLoading(true)
    try {
      const res = await employeesAPI.list(pageArg, search)
      const parsed = parsePaginatedResponse(res.data)
      setEmployees(parsed.results)
      setCount(parsed.count)
      setHasNext(parsed.hasNext)
      setHasPrev(parsed.hasPrev)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load employees.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadEmployees(searchTerm, page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    districtsAPI.listAll().then((res) => setDistricts(res.data.results || res.data)).catch(() => {})
    centersAPI.listAll().then((res) => setCenters(res.data.results || res.data)).catch(() => {})
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      loadEmployees(searchTerm, 1)
    }, 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const openAddModal = () => {
    setEditingEmployee(null)
    setForm({ ...emptyForm })
    setPhotoFile(null)
    setPhotoPreview(null)
    setEmailError('')
    setMobileError('')
    setShowPassword(false)
    setShowAddModal(true)
  }

  const openEditModal = (emp: any) => {
    setEditingEmployee(emp)
    setForm({
      employee_id: emp.employee_id,
      full_name: emp.full_name,
      email: emp.email || '',
      mobile: emp.mobile || '',
      password: '',
      role: emp.role || 'EMPLOYEE',
      designation: emp.profile?.designation || '',
      district: emp.profile?.district ? String(emp.profile.district) : '',
      training_centre: emp.profile?.training_centre ? String(emp.profile.training_centre) : '',
      employment_status: emp.profile?.employment_status || 'ACTIVE',
    })
    setPhotoFile(null)
    setPhotoPreview(emp.photo || null)
    setEmailError('')
    setMobileError('')
    setShowPassword(false)
    setShowAddModal(true)
  }

  const openPhotoModal = (photo: string | null, name: string) => {
    if (photo) {
      setSelectedPhoto(photo)
      setSelectedEmployeeName(name)
      setShowPhotoModal(true)
    }
  }

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  const validateEmail = (email: string) => {
    if (!email || email.trim() === '') {
      setEmailError('Email is required.')
      return false
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address (e.g., example@gmail.com).')
      return false
    }
    setEmailError('')
    return true
  }

  const validateMobile = (mobile: string) => {
    if (!mobile || mobile.trim() === '') {
      setMobileError('Mobile number is required.')
      return false
    }
    const mobileRegex = /^\d{10}$/
    if (!mobileRegex.test(mobile)) {
      setMobileError('Please enter a valid 10-digit mobile number (e.g., 9876543210).')
      return false
    }
    setMobileError('')
    return true
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setForm({ ...form, email: value })
    if (value) {
      validateEmail(value)
    } else {
      setEmailError('Email is required.')
    }
  }

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setForm({ ...form, mobile: value })
    if (value) {
      validateMobile(value)
    } else {
      setMobileError('Mobile number is required.')
    }
  }

  const handleEmailBlur = () => {
    if (form.email) {
      validateEmail(form.email)
    } else {
      setEmailError('Email is required.')
    }
  }

  const handleMobileBlur = () => {
    if (form.mobile) {
      validateMobile(form.mobile)
    } else {
      setMobileError('Mobile number is required.')
    }
  }

  const validateForm = () => {
    const requiredFields = {
      employee_id: 'Employee ID',
      full_name: 'Full Name',
      email: 'Email',
      mobile: 'Mobile',
      role: 'Role',
      designation: 'Designation',
      district: 'District',
      training_centre: 'Training Centre',
      employment_status: 'Employment Status'
    }

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!form[field as keyof typeof form] || form[field as keyof typeof form].toString().trim() === '') {
        toastError(`${label} is required.`)
        return false
      }
    }

    if (!editingEmployee && (!form.password || form.password.trim() === '')) {
      toastError('Password is required for new employees.')
      return false
    }

    if (!validateEmail(form.email)) {
      toastError(emailError || 'Please enter a valid email address.')
      return false
    }

    if (!validateMobile(form.mobile)) {
      toastError(mobileError || 'Please enter a valid 10-digit mobile number.')
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setIsSaving(true)
    try {
      const payload: any = {
        employee_id: form.employee_id,
        full_name: form.full_name,
        email: form.email || null,
        mobile: form.mobile,
        role: form.role,
        profile: {
          designation: form.designation,
          district: form.district ? Number(form.district) : null,
          training_centre: form.training_centre ? Number(form.training_centre) : null,
          employment_status: form.employment_status,
        },
      }
      if (form.password) payload.password = form.password

      let targetId = editingEmployee?.id
      if (editingEmployee) {
        await employeesAPI.update(editingEmployee.id, payload)
      } else {
        const res = await employeesAPI.create(payload)
        targetId = res.data.id
      }

      if (photoFile && targetId) {
        setIsUploadingPhoto(true)
        try {
          await employeesAPI.uploadPhoto(targetId, photoFile)
        } catch (photoErr: any) {
          toastError(getErrorMessage(photoErr, 'Employee saved, but the photo upload failed.'))
        } finally {
          setIsUploadingPhoto(false)
        }
      }

      success(editingEmployee ? 'Employee updated successfully.' : 'Employee added successfully.')
      setShowAddModal(false)
      setPhotoFile(null)
      setPhotoPreview(null)
      setEmailError('')
      setMobileError('')
      setShowPassword(false)
      await loadEmployees(searchTerm)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save employee.'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async (emp: any) => {
    const activating = !emp.is_active
    if (!confirm(`${activating ? 'Activate' : 'Deactivate'} ${emp.full_name}? Their attendance and report history is kept either way — deactivating only blocks login.`)) return
    try {
      if (activating) await employeesAPI.activate(emp.id)
      else await employeesAPI.deactivate(emp.id)
      success(`Employee ${activating ? 'activated' : 'deactivated'} successfully.`)
      await loadEmployees(searchTerm)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to update employee status.'))
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const res = await employeesAPI.downloadTemplate()
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'employee_bulk_upload_template.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to download template.'))
    }
  }

  const handleBulkUpload = async () => {
    if (!uploadFile) return
    setIsSaving(true)
    try {
      const res = await employeesAPI.bulkUpload(uploadFile)
      setUploadResult(res.data)
      if (res.data.error_count > 0) {
        toastError(
          `Bulk upload completed with ${res.data.error_count} failed row(s). See the detailed errors below.`
        )
      } else {
        success(`Successfully uploaded ${res.data.success_count} employee(s).`)
      }
      await loadEmployees(searchTerm)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Bulk upload failed.'))
    } finally {
      setIsSaving(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const getRoleStyles = (role: string) => {
    if (role === 'ADMIN') return 'bg-blue-50 text-blue-700 ring-blue-200'
    if (role === 'VIEWER') return 'bg-amber-50 text-amber-700 ring-amber-200'
    return 'bg-neutral-100 text-neutral-700 ring-neutral-200'
  }

  const getRoleLabel = (role: string) => {
    if (role === 'ADMIN') return 'Admin'
    if (role === 'VIEWER') return 'Viewer'
    return 'Employee'
  }

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-7">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Workforce
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Employees
          </h1>
          <p className="text-sm text-neutral-500">Manage staff members and their assignments</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowUploadModal(true); setUploadResult(null); setUploadFile(null) }}
            className="group flex items-center gap-2 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5"
          >
            <Upload className="w-4 h-4 transition-transform group-hover:scale-110" />
            Bulk Upload
          </button>
          <button
            onClick={openAddModal}
            className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4 transition-transform group-hover:rotate-90" />
            Add Employee
          </button>
        </div>
      </div>

      {/* ── Search + stats strip ── */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 group">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name, employee ID, email or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
          />
        </div>
        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-4 py-2.5 shadow-sm">
          <Users className="w-4 h-4 text-[#C24A0A]" />
          <span className="text-sm font-semibold text-neutral-800 tabular-nums">{count}</span>
          <span className="text-xs text-neutral-500">total</span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50/80 border-b border-neutral-200/70">
              <tr>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Sr.</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider"></th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Employee ID</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Name</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Contact</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Role</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Designation</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
                      <p className="text-sm text-neutral-500">Loading employees...</p>
                    </div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center">
                        <Users className="w-7 h-7 text-neutral-400" />
                      </div>
                      <p className="text-sm font-medium text-neutral-700">No employees found</p>
                      <p className="text-xs text-neutral-400">Try adjusting your search or add a new employee.</p>
                    </div>
                  </td>
                </tr>
              ) : employees.map((emp, index) => (
                <tr
                  key={emp.id}
                  className="border-b border-neutral-100 last:border-b-0 hover:bg-[#FAF6EE]/60 transition-colors group"
                >
                  <td className="px-5 py-4 text-sm text-neutral-500 tabular-nums">{(page - 1) * 10 + index + 1}</td>
                  <td className="px-5 py-4">
                    {emp.photo ? (
                      <img
                        src={emp.photo}
                        alt={emp.full_name}
                        className="w-10 h-10 rounded-full object-cover cursor-pointer ring-2 ring-white shadow-sm hover:ring-[#E4670F]/40 transition-all"
                        onClick={() => openPhotoModal(emp.photo, emp.full_name)}
                        title="Click to view full size"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-sm font-bold text-white ring-2 ring-white shadow-sm">
                        {emp.full_name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-900 bg-neutral-100 rounded-md px-2 py-0.5 tabular-nums">
                      <BadgeCheck className="w-3.5 h-3.5 text-neutral-400" />
                      {emp.employee_id}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-neutral-900 truncate max-w-[180px]">{emp.full_name}</p>
                  </td>
                  <td className="px-5 py-4">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 text-sm text-neutral-700">
                        <Mail className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        <span className="truncate max-w-[180px]">{emp.email || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-neutral-500">
                        <Phone className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                        <span className="tabular-nums">{emp.mobile || '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold ring-1 ${getRoleStyles(emp.role)}`}>
                      {getRoleLabel(emp.role)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-neutral-700">
                    {emp.profile?.designation || <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                      emp.is_active
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${emp.is_active ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openEditModal(emp)}
                        title="Edit"
                        className="p-2 text-neutral-500 hover:text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-lg transition-all"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(emp)}
                        title={emp.is_active ? 'Deactivate (blocks login, keeps records)' : 'Activate'}
                        className={`p-2 rounded-lg transition-all ${
                          emp.is_active
                            ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50'
                            : 'text-neutral-500 hover:text-emerald-600 hover:bg-emerald-50'
                        }`}
                      >
                        {emp.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} onPageChange={setPage} count={count} hasNext={hasNext} hasPrev={hasPrev} />

      {/* ── Photo Modal ── */}
      {showPhotoModal && selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
          onClick={() => setShowPhotoModal(false)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowPhotoModal(false)}
              className="absolute -top-12 right-0 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-neutral-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center">
                  <UserIcon className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  {selectedEmployeeName}
                </h3>
              </div>
              <div className="p-4 flex items-center justify-center bg-neutral-50">
                <img
                  src={selectedPhoto}
                  alt={selectedEmployeeName}
                  className="max-w-full max-h-[70vh] object-contain rounded-xl"
                />
              </div>
              <div className="p-4 border-t border-neutral-200 flex justify-end">
                <button
                  onClick={() => setShowPhotoModal(false)}
                  className="px-4 py-2 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  {editingEmployee ? <Edit className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  {editingEmployee ? 'Edit Employee' : 'Add Employee'}
                </h2>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 overflow-y-auto space-y-3.5">
              {/* Photo */}
              <div className="flex flex-col items-center gap-2 pb-2">
                <div className="relative">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Employee"
                      className="w-24 h-24 rounded-full object-cover ring-4 ring-[#E4670F]/10"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-neutral-100 ring-4 ring-neutral-50 flex items-center justify-center">
                      <UserIcon className="w-10 h-10 text-neutral-400" />
                    </div>
                  )}
                  <label
                    htmlFor="employee-photo-input"
                    className="absolute bottom-0 right-0 w-8 h-8 bg-gradient-to-br from-[#F0821F] to-[#A31621] hover:from-[#E4670F] hover:to-[#7A1F1F] rounded-full flex items-center justify-center cursor-pointer border-2 border-white shadow-md transition-all hover:scale-110"
                    title="Upload photo"
                  >
                    <Camera className="w-4 h-4 text-white" />
                  </label>
                  <input
                    id="employee-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </div>
                <p className="text-xs text-neutral-400">Square photo recommended (optional)</p>
              </div>

              <input
                type="text"
                placeholder="Employee ID *"
                value={form.employee_id}
                disabled={!!editingEmployee}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all disabled:bg-neutral-100 disabled:text-neutral-500"
              />
              <input
                type="text"
                placeholder="Full Name *"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
              />

              {/* Email */}
              <div>
                <input
                  type="text"
                  placeholder="Email * (e.g., example@gmail.com)"
                  value={form.email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  className={`w-full px-3.5 py-2.5 bg-neutral-50 border rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 outline-none transition-all ${
                    emailError
                      ? 'border-red-400 focus:ring-red-500/10 focus:border-red-500'
                      : 'border-neutral-200 focus:ring-[#E4670F]/10 focus:border-[#E4670F]'
                  }`}
                />
                {emailError && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {emailError}
                  </p>
                )}
              </div>

              {/* Mobile */}
              <div>
                <input
                  type="text"
                  placeholder="Mobile * (10 digits, e.g., 9876543210)"
                  value={form.mobile}
                  onChange={handleMobileChange}
                  onBlur={handleMobileBlur}
                  className={`w-full px-3.5 py-2.5 bg-neutral-50 border rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 outline-none transition-all ${
                    mobileError
                      ? 'border-red-400 focus:ring-red-500/10 focus:border-red-500'
                      : 'border-neutral-200 focus:ring-[#E4670F]/10 focus:border-[#E4670F]'
                  }`}
                />
                {mobileError && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {mobileError}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={editingEmployee ? 'New password (leave blank to keep)' : 'Password *'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 pr-11 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Role *</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                >
                  <option value="EMPLOYEE">Employee</option>
                  <option value="ADMIN">Admin</option>
                  <option value="VIEWER">Viewer (read-only)</option>
                </select>
              </div>

              <input
                type="text"
                placeholder="Designation *"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
              />

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">District *</label>
                <select
                  value={form.district}
                  onChange={(e) => setForm({ ...form, district: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                >
                  <option value="">Select District</option>
                  {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Training Centre *</label>
                <select
                  value={form.training_centre}
                  onChange={(e) => setForm({ ...form, training_centre: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                >
                  <option value="">Select Training Centre</option>
                  {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Employment Status *</label>
                <select
                  value={form.employment_status}
                  onChange={(e) => setForm({ ...form, employment_status: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ON_LEAVE">On Leave</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="TERMINATED">Terminated</option>
                </select>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isUploadingPhoto || !!emailError || !!mobileError}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                {isUploadingPhoto ? 'Uploading photo...' : isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Upload Modal ── */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  <Upload className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  Bulk Upload
                </h2>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <button
                onClick={handleDownloadTemplate}
                className="group w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium transition-all"
              >
                <Download className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                Download Sample Template
              </button>

              <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                Fill in the downloaded template with employee rows, then upload it below.
                Leave the "password" column blank to auto-generate one — all uploaded
                rows are created with the Employee role (bulk upload never creates admins).
              </p>

              <input
                type="file" accept=".csv,.xlsx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-neutral-600 mb-4 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#E4670F]/10 file:text-[#C24A0A] hover:file:bg-[#E4670F]/20 file:cursor-pointer file:transition-colors"
              />

              {uploadResult && (
                <div className="mb-4">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-neutral-400 font-semibold">Total</p>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums">{uploadResult.total_rows}</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Success</p>
                      <p className="text-lg font-bold text-emerald-700 tabular-nums">{uploadResult.success_count}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-red-600 font-semibold">Failed</p>
                      <p className="text-lg font-bold text-red-700 tabular-nums">{uploadResult.error_count}</p>
                    </div>
                  </div>

                  {uploadResult.row_errors?.length > 0 && (
                    <div className="border border-red-200 bg-red-50 rounded-xl max-h-64 overflow-y-auto">
                      <div className="sticky top-0 bg-red-100/90 backdrop-blur-sm px-3 py-2 font-semibold text-red-800 text-xs uppercase tracking-wide border-b border-red-200">
                        Upload Errors
                      </div>
                      {uploadResult.row_errors.map((err: any, index: number) => (
                        <div key={index} className="px-3 py-2.5 border-b border-red-100 last:border-b-0">
                          <div className="font-semibold text-red-700 text-sm">Row {err.row}</div>
                          <ul className="list-disc list-inside text-xs text-red-600 mt-1 space-y-0.5">
                            {err.errors.map((message: string, i: number) => (
                              <li key={i}>{message}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Close
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!uploadFile || isSaving}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                {isSaving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}